import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react'
import { api } from '../../lib/api.js'
import { useOnline } from '../../lib/useOnline.js'
import { toast } from '../../components/feedback/Toast.jsx'
import TapSecret from './TapSecret.jsx'
import LoginForm from './LoginForm.jsx'
import SwipeRow from '../../components/SwipeRow.jsx'

// Imported entries often carry their URL in the notes prose rather than the
// url field — make those clickable too, in a new tab like everything else.
const URL_RE = /(https?:\/\/[^\s<>"')]+)/g

function LinkifyText({ text }) {
  return text.split(URL_RE).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer" className="break-all text-link hover:underline">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

const withProtocol = (u) => (/^[a-z][a-z0-9+.-]*:/i.test(u) ? u : `https://${u}`)

/**
 * One component, three doors: the generic Logins page, the Managerial
 * section's Logins page (`restricted` prop, admin-only, mounted at
 * /management/logins — Charles, 2026-08-03), and Embargoed Projects
 * Passwords under Client Info (`embargo` prop, /clients/embargo-logins —
 * Charles, 2026-09-29, replacing a Google Sheet). The lists never mix — the
 * server splits them. The embargo page follows the generic page's rules
 * exactly: everyone signed in sees it, editors change it.
 */
export default function LoginsPage({ restricted = false, embargo = false }) {
  const qc = useQueryClient()
  const [filter, setFilter] = useState('')
  const [form, setForm] = useState(null) // null | {} (new) | credential (edit)
  const online = useOnline()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({
    queryKey: restricted ? ['credentials', 'restricted'] : embargo ? ['credentials', 'embargo'] : ['credentials'],
    queryFn: () => api(`/credentials${restricted ? '?restricted=1' : embargo ? '?collection=embargo' : ''}`),
  })
  // What a new login starts as on this page.
  const fresh = restricted ? { restricted: true } : embargo ? { collection: 'embargo' } : {}
  const title = restricted ? 'Managerial Logins' : embargo ? 'Embargoed Projects Passwords' : 'Logins'

  const isAdmin = me.data?.user?.role === 'admin'
  // Offline is read-only; reveals also need the server (secrets are never
  // cached on the device), so an offline tap fails with a toast either way.
  const canEdit = (restricted ? isAdmin : ['admin', 'editor'].includes(me.data?.user?.role)) && online

  // Non-admins have no business here — bounce without acknowledging it exists.
  if (restricted && me.data?.user && !isAdmin) return <Navigate to="/logins" replace />

  const rows = (data?.credentials || []).filter((c) => {
    const f = filter.trim().toLowerCase()
    if (!f) return true
    return [c.label, c.username, c.tags].some((v) => v && v.toLowerCase().includes(f))
  })

  async function remove(c) {
    if (!window.confirm(`Delete “${c.label}”? The value is gone for everyone.`)) return
    try {
      await api(`/credentials/${c.id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['credentials'] })
      toast('Login deleted')
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  return (
    <div>
      {/* It lives under Client Info — say so, and give desktop (no back
          chevron there) a way home. Same look as a page's breadcrumbs. */}
      {embargo && (
        <nav className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted">
          <Link to="/clients" className="hover:text-ink">Client Info</Link>
        </nav>
      )}
      <div className="mb-1 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        {canEdit && (
          <button
            onClick={() => setForm(fresh)}
            className="tap flex shrink-0 items-center gap-1.5 rounded-xl bg-navy px-3.5 py-2 text-sm font-medium text-white"
          >
            <Plus size={15} /> New login
          </button>
        )}
      </div>
      <p className="mb-4 border-b border-hairline pb-3 text-sm text-muted">Tap a password to reveal and copy it. Tap again to hide it.</p>

      <input
        type="search"
        placeholder="Filter…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="tap mb-4 w-full rounded-xl border border-hairline bg-grouped px-4 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
      />

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <div className="card divide-y divide-hairline overflow-hidden">
          {rows.map((c) => (
            <MaybeSwipe key={c.id} enabled={canEdit} onDelete={() => remove(c)}>
            <div className="px-4 py-3">
              {/* Line 1: the name owns the full width — it must be readable
                  at a glance on a phone, so it wraps rather than truncates
                  (Charles, 2026-08-02). Actions ride along on the right. */}
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 text-sm font-medium text-ink">
                  <span className="break-words">{c.label}</span>
                  {c.restricted && !restricted && (
                    <span className="ml-1.5 inline-block rounded border border-hairline px-1 py-px align-[2px] text-[9px] font-semibold uppercase tracking-wide text-faint">
                      admin
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div className="-mt-1 flex shrink-0 items-center">
                    <button onClick={() => setForm(c)} title="Edit" className="tap flex items-center justify-center rounded-lg text-faint active:bg-grouped active:text-ink">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(c)} title="Delete" className="tap flex items-center justify-center rounded-lg text-faint active:bg-grouped active:text-danger">
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              {/* Labeled rider-form lines (Charles, 2026-08-03): every value
                  says what it is — username / password / link — with the
                  dotted leader carrying the eye across. Username copies on
                  tap, same contract as the password. */}
              {(c.username || c.hasSecret || c.url) && (
                <div className="mt-2 space-y-1.5">
                  {c.username && (
                    <FieldRow label="username">
                      <button
                        type="button"
                        title="Copy username"
                        onClick={async () => {
                          await navigator.clipboard.writeText(c.username).catch(() => {})
                          toast('Username copied')
                        }}
                        className="inline-flex min-w-0 items-center gap-1.5 text-sm text-ink active:opacity-60"
                      >
                        <span className="truncate">{c.username}</span>
                        <Copy size={12} className="shrink-0 text-faint" />
                      </button>
                    </FieldRow>
                  )}
                  {c.hasSecret && (
                    <FieldRow label="password">
                      <TapSecret credentialId={c.id} />
                    </FieldRow>
                  )}
                  {c.url && (
                    <FieldRow label="link">
                      <a
                        href={withProtocol(c.url)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-w-0 items-center gap-1.5 text-sm text-link active:opacity-60"
                      >
                        <span className="truncate">{c.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                        <ExternalLink size={12} className="shrink-0" />
                      </a>
                    </FieldRow>
                  )}
                </div>
              )}
              {(c.pageTitle || c.notes) && (
                <div className="mt-2 space-y-0.5 text-xs">
                  {c.pageTitle && c.spaceSlug && (
                    <Link to={`/${c.spaceSlug}/${c.pageSlug}`} className="block text-muted hover:text-ink">
                      used in “{c.pageTitle}” →
                    </Link>
                  )}
                  {c.notes && (
                    <p className="break-words text-faint">
                      <LinkifyText text={c.notes} />
                    </p>
                  )}
                </div>
              )}
            </div>
            </MaybeSwipe>
          ))}
          {rows.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted">
              {filter.trim() || data?.credentials?.length ? 'No matches.' : canEdit ? 'No logins yet — tap New login to add the first.' : 'No logins yet.'}
            </p>
          )}
        </div>
      )}

      {form && <LoginForm initial={form.id ? form : fresh} onClose={() => setForm(null)} />}
    </div>
  )
}

/** Swipe-left-to-delete for editors; viewers get the plain row. */
function MaybeSwipe({ enabled, onDelete, children }) {
  if (!enabled) return children
  return <SwipeRow onDelete={onDelete}>{children}</SwipeRow>
}

/** label ····· value — the rider-form line, now with the label spelled out. */
function FieldRow({ label, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-[4.4rem] shrink-0 text-[10px] font-semibold uppercase tracking-[1px] text-faint">
        {label}
      </span>
      <span aria-hidden className="min-w-3 flex-1 border-b-[1.5px] border-dotted border-hairline" />
      {children}
    </div>
  )
}
